import { Injectable } from '@angular/core';

import { IField, IFilter, IFilterCriteria, AzavistaSharedService, IFilterTree, IFilterTreeNode, CriteriaRelationOperator } from '@azavista/components/shared';
import { GlobalReportColumnEntityType, IDependency, IDependencyCriteria, IDependencyV2, convertDependencyToV2 } from '@azavista/servicelib';
import { FilterCriteriaTreeDialogData } from '@azavista/components/filter-criteria-tree';
import { SharedService, pick } from 'src/app/shared/shared.service';
import { TRANSLATION_MAP } from 'src/app/shared/const';
import { TranslationKey } from 'src/app/shared/enums';

/**
 * IFilterCriteria contains much more information than the server needs.
 *
 * We will remove allOperators and will leave only the id of the field
*/
export const filterCriteriaToDependencyCriteria = (criteria: IFilterCriteria): IDependencyCriteria => {
    return {
        field: { id: criteria.field.id } as IField,
        operator: criteria.operator as any,
        value: criteria.value
    };
};

export const dependencyCriteriaToFilterCriteria = (criteria: IDependencyCriteria, filterCriteriaField: IField, cmpSharedSvc: AzavistaSharedService): IFilterCriteria => {
    return {
        ...criteria as any,
        field: filterCriteriaField,
        allOperators: cmpSharedSvc.getAllOperatorsByField(filterCriteriaField)
    };
};

export const filterToDependencyV2 = (
    filter: IFilter,
    filterId: string
): IDependencyV2 => {
    return {
        id: filterId, name: filter.name,
        version: 'v2',
        criteria: [filter.criteria.map(filterCriteriaToDependencyCriteria)]
    };
};

export const filterTreeToDependencyV2 = (
    filter: IFilterTree<2, true>,
    filterId: string
): IDependencyV2 => {
    const { criteriaTree, name } = filter;
    const convertedCriteria = criteriaTree.criteria
        .map(tree => tree.criteria
            .map(filterTreeToDependencyCriteria)
        );
    return {
        id: filterId, name,
        version: 'v2',
        criteria: convertedCriteria
    };
};

const filterTreeToDependencyCriteria = <ValueType = any>(
    filterNode: IFilterTreeNode<ValueType>
): IDependencyCriteria<ValueType> => {
    return filterNode as any;
};

export const dependencyV2ToFilterTree = (
    dependency: IDependencyV2,
    filterId: string,
): IFilterTree<2, true> => {
    const { criteria } = dependency;
    return {
        ...dependency,
        id: filterId,
        nodeOnlyOnLastLevel: true,
        treeDepth: 2,
        criteriaTree: {
            operator: CriteriaRelationOperator.or,
            criteria: criteria.map(tree => ({
                operator: CriteriaRelationOperator.and,
                criteria: tree.map(dependencyCriteriaToFilterTree)
            }))
        }
    };
};

const dependencyCriteriaToFilterTree = <ValueType = any>(
    dependencyCriteria: IDependencyCriteria<ValueType>
): IFilterTreeNode<ValueType> => {
    return dependencyCriteria as any;
};

export type FilterFieldsParams = {
    participantFields: IField[],
    eventFields?: IField[],
    eventSettingsFields?: IField[],
    activityIdsField?: IField,
    workflowFields?: IField[],
    productFields?: IField[]
}

export const formatFieldsToFilterFields =
<Params extends FilterFieldsParams>(params: Params, sharedSvc: SharedService): Params => {
    const result: Required<FilterFieldsParams> = {
        participantFields: params.participantFields?.map(field => {
            return {
                ...field,
                id: `${GlobalReportColumnEntityType.participant}_${field.id}`,
                label: field.label,
                category: sharedSvc.translate(TRANSLATION_MAP.PARTICIPANT),
            };
        }),
        eventSettingsFields: params.eventSettingsFields.map(field => {
            return {
                ...field,
                id: `EventSettings_${field.id}`,
                label: field.label,
                category: sharedSvc.translate(TRANSLATION_MAP.EVENT_SETTINGS)
            };
        }),
        eventFields: params.eventFields?.map(field => {
            return {
                ...field,
                id: `${GlobalReportColumnEntityType.event}_${field.id}`,
                label: field.label,
                category: sharedSvc.translate(TRANSLATION_MAP.EVENT)
            };
        }),
        activityIdsField: {
            ...params.activityIdsField,
            category: sharedSvc.translate(TRANSLATION_MAP.ACTIVITY)
        },
        productFields: params.productFields?.map(field => {
            return {
                ...field,
                label: field.label,
                category: sharedSvc.translate(TRANSLATION_MAP.PRODUCT)
            };
        }),
        workflowFields: params.workflowFields
    };
    return pick(result, Object.keys(params) as any);
};

export const formatFieldsToFilterFieldsArray =
<Params extends FilterFieldsParams>(params: Params, sharedSvc: SharedService): IField[] => {
    const filterFieldsObject = formatFieldsToFilterFields(params, sharedSvc);
    const result: IField[] = [];Object
    .values(filterFieldsObject)
    .forEach(fields => {
        const arrayFields = Array.isArray(fields) ? fields : [fields].filter(item => !!item);
        result.push(...arrayFields);
    });
    return result;
};

@Injectable({
    providedIn: 'root'
})
export class EventContentWebsiteService {
    constructor(private sharedSvc: SharedService) {
    }

    createEventDependenciesDialogData(
        dependency: IDependency,
        formattedFields: IField[],
        isCreateNew: boolean,
    ): FilterCriteriaTreeDialogData<2, true> {
        formattedFields.forEach(x => {
            if (x.schema && x.schema.type) {
                x.editable = true;
            }
        });

        const filter = dependencyV2ToFilterTree(convertDependencyToV2(dependency), dependency.id);
        if (isCreateNew) {
            filter.criteriaTree = null;
        }
        return {
            availableFields: formattedFields,
            readonly: false,
            allowedCriteriaRelationPerTreeLevel: {
                0: [CriteriaRelationOperator.or],
                1: [CriteriaRelationOperator.and]
            },
            dialogTitle: this.sharedSvc.translate(TranslationKey.addNewDependencyFilters),
            filter: filter,
        };
    }

    async confirmDelete(): Promise<boolean> {
        const confirmationResult = await this.sharedSvc.showConfirmationDialog(
            this.sharedSvc.translate(TranslationKey.deleteWebDependencyDialogTitle), [
            this.sharedSvc.translate(TranslationKey.deleteWebDependencyDialogAreYouSure),
            this.sharedSvc.translate(TranslationKey.wontBeAbleToRestoreData)
        ]);
        return !!confirmationResult;
    }
}
